import React, { useState, useEffect } from 'react';
import apiDefinitions from "../../../../core/api/apiDefinitions";
import { showSuccessAlert, showDeleteConfirmation, showItemExistsError, showItemAlreadyUsed } from '../../../../core/alert/sweetAlertConfig';

const AddVehicleClass: React.FC = () => {
  const [vehicleClassData, setVehicleClassData] = useState<any>(null);
  const [isVehicleClassLoading, setIsVehicleClassLoading] = useState(true);
  const [selectedVehicleClassId, setSelectedVehicleClassId] = useState("");
  const [newVehicleClassName, setNewVehicleClassName] = useState("");
  const [formErrors, setFormErrors] = useState({
    vehicleClassName: "",
    selectedVehicleClass: "",
  });
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openFail, setOpenFail] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Operation Failed");

  const handleCloseSuccess = () => setOpenSuccess(false);
  const handleCloseFail = () => setOpenFail(false);

  // Auto-dismiss success notification
  useEffect(() => {
    if (openSuccess) {
      const timer = setTimeout(() => setOpenSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [openSuccess]);

  // Fetch vehicle classes on component mount
  useEffect(() => {
    fetchVehicleClasses();
  }, []);

  const fetchVehicleClasses = () => {
    setIsVehicleClassLoading(true);
    apiDefinitions.getAllVehicleClasses()
      .then((response) => {
        if (response.data.status === "S1000" && response.data.data) {
          setVehicleClassData(response.data);
        } else {
          setErrorMessage(response.data.statusDescription || "Failed to load vehicle classes");
          setOpenFail(true);
        }
        setIsVehicleClassLoading(false);
      })
      .catch((error) => {
        const message = error.response?.data?.statusDescription || error.response?.data?.message || "Failed to load vehicle classes: Server error";
        setErrorMessage(message);
        setOpenFail(true);
        setIsVehicleClassLoading(false);
      });
  };

  const validateVehicleClassForm = (name: string) => {
    const errors = { vehicleClassName: "", selectedVehicleClass: "" };
    let isValid = true;

    if (!name.trim()) {
      errors.vehicleClassName = "Vehicle class name is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const validateSelectedVehicleClass = () => {
    const errors = { vehicleClassName: "", selectedVehicleClass: "" };
    let isValid = true;

    if (!selectedVehicleClassId) {
      errors.selectedVehicleClass = "Please select a vehicle class";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleAddVehicleClassSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateVehicleClassForm(newVehicleClassName)) return;

    apiDefinitions.createVehicleClass({ className: newVehicleClassName })
      .then((response: { data: any }) => {
        if (response.data.status === "S1000") {
          showSuccessAlert("Vehicle class added successfully");
          setNewVehicleClassName("");
          fetchVehicleClasses();
        } else {
           showItemExistsError("A vehicle class with this name already exists.");
        }
      })
      .catch((error: any) => {
        showItemExistsError("A vehicle class with this name already exists.");
      });
  };

  const handleDeleteVehicleClass = () => {
    if (!validateSelectedVehicleClass()) return;
     showDeleteConfirmation(() => {
    apiDefinitions.deleteVehicleClass(parseInt(selectedVehicleClassId))
      .then((response) => {
        if (response.data.status === "S1000") {
          setOpenSuccess(true);
          setSelectedVehicleClassId("");
          fetchVehicleClasses();
        } else {
          showItemAlreadyUsed("Sorry, this class is already in use and cannot be deleted.");
        }
      })
      .catch((error) => {
        showItemAlreadyUsed("Sorry, this class is already in use and cannot be deleted.");
      });
    });
  };

  const handleVehicleClassChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedVehicleClassId(e.target.value);
    setFormErrors((prev) => ({ ...prev, selectedVehicleClass: "" }));
  };

  return (
    <>
      <div className="card">
        <div className="card-body pb-0">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h4 className="fs-20 mb-0">Add Vehicle Class</h4>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="row" style={{ gap: '30px' }}>
                <div className="col-md-5">
                  <div className="mb-4">
                    <label className="col-form-label mb-2"><h5>Vehicle Class</h5></label>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '300px', padding: '10px 15px' }}
                        value={newVehicleClassName}
                        onChange={(e) => setNewVehicleClassName(e.target.value)}
                        placeholder="Enter vehicle class name"
                      />
                      <button
                        type="button"
                        className="btn btn-success ms-3"
                        data-bs-toggle="modal"
                        data-bs-target="#vehicle-class-modal"
                        style={{ padding: '10px 12px', whiteSpace: 'nowrap', display: 'inline-block' }}
                      >
                        Add Class
                      </button>
                    </div>
                    {formErrors.vehicleClassName && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehicleClassName}</div>
                    )}
                    <select
                      className="form-select mt-3"
                      value={selectedVehicleClassId}
                      onChange={handleVehicleClassChange}
                      style={{ width: '300px', padding: '10px' }}
                    >
                      <option value="">Select Vehicle Class</option>
                      {isVehicleClassLoading ? (
                        <option disabled>Loading...</option>
                      ) : (
                        vehicleClassData?.data?.map((vehicleClass: any) => (
                          <option key={vehicleClass.id} value={vehicleClass.id}>{vehicleClass.className}</option>
                        ))
                      )}
                    </select>
                    {formErrors.selectedVehicleClass && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.selectedVehicleClass}</div>
                    )}
                    <div className="d-flex gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleDeleteVehicleClass}
                        style={{ padding: '8px 16px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Vehicle Class Modal */}
      <div className="modal fade" id="vehicle-class-modal" tabIndex={-1} aria-labelledby="add-vehicle-class" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="add-vehicle-class">Add New Vehicle Class</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddVehicleClassSubmit}>
                <div className="mb-3">
                  <label htmlFor="newVehicleClassName" className="form-label">Vehicle Class Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="newVehicleClassName"
                    value={newVehicleClassName}
                    onChange={(e) => setNewVehicleClassName(e.target.value)}
                    placeholder="Enter vehicle class name (e.g., Motor Car)"
                    style={{ width: '100%', padding: '10px 15px' }}
                  />
                  {formErrors.vehicleClassName && (
                    <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.vehicleClassName}</div>
                  )}
                </div>
                <button type="submit" className="btn btn-success" data-bs-dismiss="modal" style={{ padding: '10px 20px' }}>
                  Add Vehicle Class
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Success Notification */}
      {openSuccess && (
        <div
          className="alert alert-success alert-dismissible fade show"
          role="alert"
          style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 1100 }}
        >
          Operation successful
          <button
            type="button"
            className="btn-close"
            onClick={handleCloseSuccess}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Error Notification */}
      {openFail && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
          style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 1100 }}
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={handleCloseFail}
            aria-label="Close"
          ></button>
        </div>
      )}
    </>
  );
};

export default AddVehicleClass;